<template>
  <div class="team-buy">
    <Alert
      v-if="isRightType16 && !isUS"
      :message="
        $t(
          'teamlightrightrightsname-de-jia-gou-yan-bi-zong-shang-xian-wei-teamlightrightaddmorecreditcoinslimit-0-dang-qian-ke-jia-gou-remaincoinscount-0-yan-bi',
          [
            teamLightRight?.rights_name,
            teamLightRight?.add_more_credit_coins_limit || 0,
            remainCoinsCount || 0,
          ],
        )
      "
      :type="'warning'"
      show-icon
      class="limit-alert"
    />
    <div
      :class="{
        global: isUS,
      }"
    >
      <div
        v-for="(item, idx) in packages"
        :key="item.id"
        :class="{
          active: item.id === picked?.id,
          disabled: getCounts(item) === 0,
        }"
        @click="handlePick(item)"
      >
        <div class="title">
          {{ item.title }}
          <Divider :variant="'solid'" />
          <span v-if="!isUS" class="sub">
            {{ $t('sheng-yu-getcountsitem', [getCounts(item)]) }}
          </span>
        </div>
        <div class="card">
          <iconpark-icon
            v-if="item.id === picked?.id"
            class="selected"
            name="xuanzhong"
          ></iconpark-icon>
          <div class="price">
            <span class="price-prefix">
              {{ formatPrice(item.price).prefix }}
            </span>
            <span class="price-num">
              {{ formatPrice(item.price).value }}
            </span>
          </div>
          <div class="coin-count">
            <span class="icon-value">
              {{ $t('gong-itemtotalcreditcoins-yan-bi', [item.total_credit_coins]) }}
              <br v-if="isUS" />
              {{
                $t('yu-ji-sheng-chan-getavailabletimecountitemtotalcreditcoins-shi-pin', [
                  getAvailableTimeCount(item.total_credit_coins),
                ])
              }}
              <Tooltip
                :placement="'right'"
                :content="
                  $t(
                    'gen-ju-quan-ping-tai-yong-hu-ping-jun-xiao-hao-ji-suan-shi-ji-xiao-hao-yan-bi-shu-yi-ge-ren-zhi-zuo-xiao-hao-wei-zhun',
                  )
                "
              />
            </span>
          </div>
        </div>
        <Alert
          v-if="getCounts(item) === 0 && !isUS"
          class="alert"
          type="warning"
          :message="$t('wu-fa-xuan-ze-gai-tao-can-yi-chao-chu-yan-bi-jia-gou-shu-liang-shang-xian')"
        />
        <Divider
          v-if="!isUS && idx !== packages.length - 1"
          class="gap"
          :variant="'text'"
          :text="'or'"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getOrderClientRemainedBuyCreditCoinsCount } from '@rendora/common/entities/order';
import { type ICreditCoinsPackage } from '@rendora/common/entities/rights';
import { getI18nService } from '@rendora/common/services/i18n';
import { useRightsStore } from '@rendora/common/store/rights';
import { IRightsType } from '@rendora/common/types/enums';
import { getGlobalConfig } from '@rendora/common/utils/config';
import { formatDuration, formatPrice } from '@rendora/common/utils/transform';
import { Alert } from '@rendora/design/components/alert/index';
import { Divider } from '@rendora/design/components/divider/index';
import { Tooltip } from '@rendora/design/components/tooltip/index';
import { computed, onMounted, ref } from 'vue';

const i18nService = getI18nService();
const $t = i18nService.t;

const { rightsDisplay } = useRightsStore();

const teamLightRight = computed(() => {
  return rightsDisplay?.rights.find(right => right.rights_type === IRightsType.TEAM_LIGHT);
});

const { isUS } = getGlobalConfig();

defineProps<{
  packages: ICreditCoinsPackage[];
  picked?: ICreditCoinsPackage;
}>();

type DurationLike = { seconds: () => number; minutes: () => number; hours: () => number };

const getAvailableTimeCount = (totalCreditCoins: number) => {
  return formatDuration(totalCreditCoins * (isUS ? 0.3 : 0.2) * 1000, (d: DurationLike) => {
    const s = d.seconds();
    const m = d.minutes();
    const h = d.hours();
    let str = '';
    if (s > 0) {
      str = $t('dseconds-miao', [d.seconds()]);
    }
    if (m > 0 || h > 0) {
      str = `${m + h * 60}${$t('fen-zhong')}${str}`;
    }
    return str;
  });
};

// 是团队入门版
const isRightType16 = computed(() => {
  return teamLightRight.value?.rights_type === IRightsType.TEAM_LIGHT;
});

const remainCoins = ref<{ remained_credit_coins: number }>();

onMounted(async () => {
  const { data } = await getOrderClientRemainedBuyCreditCoinsCount();
  remainCoins.value = data;
});

const remainCoinsCount = computed(() => {
  return remainCoins.value?.remained_credit_coins;
});

const getCounts = (item: ICreditCoinsPackage) => {
  const remained_credit_coins = remainCoins.value?.remained_credit_coins;

  if (remained_credit_coins === null) {
    return null;
  }

  return Math.floor((remained_credit_coins || 0) / item.total_credit_coins);
};

const emits = defineEmits(['change']);

const handlePick = (v: ICreditCoinsPackage) => emits('change', v);
</script>

<style lang="scss" scoped>
.team-buy {
  width: 100%;
}
.limit-alert {
  margin-bottom: 26px;
  margin-top: -14px;
}
.tip-icon {
  transform: translateY(2px);
  color: var(--x-text-1);
}
.global {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, 1fr);
}
.selected {
  position: absolute;
  right: 10px;
  top: 10px;
  font-size: 24px;
}
.title {
  font-weight: var(--x-weight-medium);
  font-size: 14px;
}
.sub {
  color: var(--x-text-1-60);
  font-weight: 400;
}
.gap {
  font-size: 14px;
  color: var(--x-text-1-50);
}
.disabled {
  pointer-events: none;

  .card {
    opacity: 0.5;
  }
}
.alert {
  font-size: 12px;
  text-align: center;
  border: none;
  margin-top: 6px;
}

.active {
  .card {
    border: 2px solid var(--x-purple-6);
    background: var(--x-purple-2);
  }
}
.card {
  position: relative;
  cursor: pointer;
  background-color: var(--x-back-3);
  border-radius: var(--x-radius-medium);
  border: 2px solid transparent;
  margin-top: 10px;
  padding: 25px 20px;
}
.price {
  color: var(--x-error-6);
  font-weight: 700;
}
.price-prefix {
  font-size: 16px;
}
.price-num {
  font-size: 28px;
}
.icon-value {
  color: var(--primary);
  font-size: 14px;
}
</style>
